import { makeAutoObservable } from "mobx";
import { IMoneyDto, IItemBalanceBreakdownDto, ORIGINAL_ITEM_PRICE_SOURCE, NEW_ITEM_PRICE_SOURCE, ORIGINAL_ITEM_PRICE_SOURCE_TRANSLATIONS_MAP, NEW_ITEM_PRICE_SOURCE_TRANSLATIONS_MAP } from "../rma-types";
import ProductCaseStore from "./product-case-store";
import OtherItemRelatedPaymentsStore from "./other-item-related-payments-store";
import CustomerCaseStore from "./customer-case-store";

class ProductCaseItemsBalanceStore {

    private _deductForOtherItems?: OtherItemRelatedPaymentsStore[];
    private _paidByOtherItems?: OtherItemRelatedPaymentsStore[];

    constructor(private _model: IItemBalanceBreakdownDto, private _productCaseStore: ProductCaseStore, private _customerCaseStore: CustomerCaseStore) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._deductForOtherItems = this._model.paid_for_other_items.map((dto) => {
            return new OtherItemRelatedPaymentsStore(dto, this._takePaymentCurrencyDataFromMoneyDto, this._customerCaseStore.getProductNameByProductCaseId);
        });
        this._paidByOtherItems = this._model.paid_by_other_items.map((dto) => {
            return new OtherItemRelatedPaymentsStore(dto, this._takePaymentCurrencyDataFromMoneyDto, this._customerCaseStore.getProductNameByProductCaseId);
        });
    }

    public get deductForOtherItems(): OtherItemRelatedPaymentsStore[] | undefined { return this._deductForOtherItems; }
    public get hasAnyDeductForItemPayment(): boolean {
        return !!this._deductForOtherItems?.find(dto => dto.hasAnyPayment);
    }

    public get paidByOtherItems(): OtherItemRelatedPaymentsStore[] | undefined { return this._paidByOtherItems; }
    public get hasAnyPaidByOtherItemPayment(): boolean {
        return !!this._paidByOtherItems?.find(dto => dto.hasAnyPayment);
    }

    public get balance(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.balance); }
    public get newItemPaymentPrice(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.new_item_price); }
    public get newItemPaymentPriceSource(): NEW_ITEM_PRICE_SOURCE | null { return this._model.new_item_price_source; }
    public get newItemPaymentPriceSourceTranslationKey(): string | null {
        if (!this._model.new_item_price_source) {
            return null;
        }
        return NEW_ITEM_PRICE_SOURCE_TRANSLATIONS_MAP[this._model.new_item_price_source];
    }
    public get priceDifferenceAdjustments(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.price_difference_adjustments); }
    public get newItemPaymentPriceDifferenceAdjustments(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.new_item_price_difference_adjustments); }
    public get newItemBxGyDiscountAdjustment(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.new_item_bxgy_discount_adjustment); }
    public get newItemAppliedDiscount(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.new_item_applied_discount); }
    public get newItemDiscounts(): string[] | undefined { return this._model.new_item_discounts; }
    public get originalItemPaidPrice(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.original_item_paid_price); }
    public get originalItemPriceForExchange(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.original_item_price_for_exchange); }
    public get originalItemPaidPriceSource(): ORIGINAL_ITEM_PRICE_SOURCE | null { return this._model.original_item_price_source; }
    public get originalItemPaidPriceSourceTranslationKey(): string | null {
        if (!this._model.original_item_price_source) {
            return null;
        }
        return ORIGINAL_ITEM_PRICE_SOURCE_TRANSLATIONS_MAP[this._model.original_item_price_source];
    }
    public get exchangeRangeAdjustment(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.exchange_range_adjustment); }
    public get refundAdjustment(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.refund_adjustments); }
    public get switchToExchangeIncentive(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.switch_to_exchange_incentive); }
    public get compareAtPriceDiscount(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.compare_at_price_discount); }
    public get compareAtPriceDiscountPercentage(): number | null { return this._model.compare_at_price_discount_percentage; }

    public get restockingFeeDeduction(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.deduction.restocking_fee); }
    public get returnLabelFeeDeduction(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.deduction.return_label_fee); }
    public get rmaFeeDeduction(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.deduction.rma_fee); }

    public get discountFee(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.discount_fee); }
    public get restockingFee(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.restocking_fee); }
    public get returnLabelFee(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.return_label_fee); }
    public get rmaFee(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.rma_fee); }
    public get cancellationFee(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.cancellation_fee); }

    public get catalogPrice(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.original_item_catalog_price_at_time_of_purchase); }
    public get compareAtPriceAmount(): IMoneyDto | undefined { return this._takePaymentCurrencyDataFromMoneyDto(this._model.compare_at_price_discount) ; }

    public get bxgyDiscountAmount(): number | undefined {return this._takePaymentCurrencyDataFromMoneyDto(this._model.applied_bxgy_discount)?.amount; }
    public get basicDiscountAmount(): number | undefined {return this._takePaymentCurrencyDataFromMoneyDto(this._model.applied_basic_discount)?.amount; }
    public get totalDiscounts(): number { return (this.basicDiscountAmount ?? 0) + (this.bxgyDiscountAmount ?? 0); }

    public get isExchange(): boolean {
        return this._productCaseStore.isExchange;
    }

    public get showBalanceBreakdown(): boolean {
        return (!!this.newItemPaymentPrice && this.newItemPaymentPrice.amount !== 0)
            || this.priceDifferenceAdjustments?.amount !== 0
            || this.newItemPaymentPriceDifferenceAdjustments?.amount !== 0
            || this.newItemBxGyDiscountAdjustment?.amount !== 0
            || this.newItemAppliedDiscount?.amount !== 0
            || (!!this.exchangeRangeAdjustment && this.exchangeRangeAdjustment.amount !== 0)
            || (!!this.refundAdjustment && this.refundAdjustment.amount !== 0)
            || (!!this.switchToExchangeIncentive && this.switchToExchangeIncentive.amount !== 0)
            || (!!this.compareAtPriceDiscount && this.compareAtPriceDiscount.amount !== 0)
            || this.hasAnyDeductForItemPayment
            || this.hasAnyPaidByOtherItemPayment
            || this.showFeesBreakdown
    }

    public get showFeesBreakdown(): boolean {
        return this.restockingFee?.amount !== 0 ||
            this.rmaFee?.amount !== 0 ||
            this.returnLabelFee?.amount !== 0 ||
            this.cancellationFee?.amount !== 0;
    }

    private _takePaymentCurrencyDataFromMoneyDto(moneyDtos: IMoneyDto[] | undefined): IMoneyDto | undefined {
        const paymentCurrencyCode = this._productCaseStore.customerCaseStore.paymentCurrencyCode;
        return (moneyDtos ?? []).find(dto => dto.currency_code === paymentCurrencyCode);
    }
}

export default ProductCaseItemsBalanceStore;
